import * as THREE from 'three'

import { GPUComputationRenderer } from 'three/examples/jsm/misc/GPUComputationRenderer.js'

import Experience from '../Experience'

import simulationShader from '../shaders/fbo/simulation.frag'
import velocityShader from '../shaders/fbo/velocity.frag'
import positionShader from '../shaders/fbo/position.frag'
import particleVertex from '../shaders/fbo/particles.vert'
import particleFragment from '../shaders/fbo/particles.frag'
import sphereShader from '../shaders/fbo/sphere.frag'

import {
  fillPositionTexture, 
  fillSpherePositionTexture, 
  fillPositions, 
  fillSphere,
  fillTerrainTexture
} from '../utils/fill'

import gsap from 'gsap'

import store from '../../store'

export default class FBO {
  constructor() {
    this.experience = new Experience()
    this.renderer = this.experience.renderer
    this.scene = this.experience.scene
    this.debug = this.experience.debug
    this.resources = this.experience.resources

    // console.log(this.resources)

    this.tier = store.benchmark.tier

    // console.log(store.sniffer)

    // Force tier for Safari
    if (store.sniffer.isSafari &&
        !store.sniffer.isDevice) this.tier = 3

    if (store.sniffer.isDevice) this.tier = 1

    // data texture width that determines the number of particles
    switch(this.tier) {
      case 1:
        this.width = 64
        break

      case 2:
        this.width = 96
        break

      case 3:
        this.width = 128
        break

      default:
        this.width = 64
    }

    this.count = this.width * this.width

    // console.log(`number of particles: ${this.width * this.width}`)

    this.init()
    this.createMesh()

    if (this.debug.active) {
      // // add debug tweaks
      // this.debug.ui.addFolder({ title: 'FBO', expanded: true })

      // let simUniforms = this.positionVariable.material.uniforms

      // this.debug.ui.addInput(simUniforms.uSpeed, 'value', { min: 0, max: 1, step: 0.01, label: 'speed' })
      // this.debug.ui.addInput(simUniforms.uFrequency, 'value', { min: 0, max: 10, step: 0.01, label: 'frequency' })
      // this.debug.ui.addInput(simUniforms.uAmplitude, 'value', { min: 0, max: 3, step: 0.01, label: 'amplitude' })

      console.log(`tier: ${this.tier}\ntexture width: ${this.width}\nnumber of particles: ${this.width * this.width}`)      
    }       

    this.animate()
  }

  init() {
    this.gpuCompute =  new GPUComputationRenderer(this.width, this.width, this.renderer.instance)

    this.velocityTexture = this.gpuCompute.createTexture()
    this.positionTexture = this.gpuCompute.createTexture()
    this.spherePosTexture = this.gpuCompute.createTexture()
    this.defaultPosTexture = this.gpuCompute.createTexture()
    this.planeTexture = this.gpuCompute.createTexture()

    fillPositionTexture(this.positionTexture)
    fillSpherePositionTexture(this.spherePosTexture)
    fillPositionTexture(this.defaultPosTexture)
    fillTerrainTexture(this.planeTexture)
    
    this.velocityVariable = this.gpuCompute.addVariable('textureVelocity', velocityShader, this.velocityTexture)
    this.positionVariable = this.gpuCompute.addVariable('texturePosition', simulationShader, this.positionTexture)
    this.sphereVariable = this.gpuCompute.addVariable('textureSphere', sphereShader, this.spherePosTexture)
    this.defaultPosVariable = this.gpuCompute.addVariable('textureDefaultPos', positionShader, this.defaultPosTexture)
    
    this.gpuCompute.setVariableDependencies(this.velocityVariable, [this.positionVariable, this.velocityVariable, this.sphereVariable, this.defaultPosVariable])
		this.gpuCompute.setVariableDependencies(this.positionVariable, [this.positionVariable, this.velocityVariable, this.sphereVariable, this.defaultPosVariable])
		this.gpuCompute.setVariableDependencies(this.sphereVariable, [this.positionVariable, this.velocityVariable, this.sphereVariable, this.defaultPosVariable])
		this.gpuCompute.setVariableDependencies(this.defaultPosVariable, [this.positionVariable, this.velocityVariable, this.sphereVariable, this.defaultPosVariable])

    this.velocityVariable.wrapS = this.velocityVariable.wrapT = THREE.RepeatWrapping    
    this.positionVariable.wrapS = this.positionVariable.wrapT = THREE.RepeatWrapping
    this.sphereVariable.wrapS = this.sphereVariable.wrapT = THREE.RepeatWrapping
    this.defaultPosVariable.wrapS = this.defaultPosVariable.wrapT = THREE.RepeatWrapping

    // Add uniforms to velocity texture
    Object.assign(this.velocityVariable.material.uniforms, {
      uTime: { value: 0 },
      uProgress: { value: 1 },
    })

    // Add uniforms to position texture
    Object.assign(this.positionVariable.material.uniforms, {
      uTime: { value: 0 },
      uSpeed: { value: 0.1 },
      uFrequency: { value: 0.87 },
      uAmplitude: { value: 1.79 },
      uProgress: { value: 1 },
      uMorph2: { value: 0 },
      uMorph3: { value: 0 },
      tPlane: { value: this.planeTexture }
    })

    // Add uniforms to def pos texture
    Object.assign(this.defaultPosVariable.material.uniforms, {
      uTime: { value: 0 },
      uProgress: { value: 1 },
    })    

    // Add uniforms to def pos texture
    Object.assign(this.sphereVariable.material.uniforms, {
      uTime: { value: 0 },
      uProgress: { value: 1 },
    })    
    
    const error = this.gpuCompute.init()
    if (error !== null ) console.error(error)
  }

  createMesh() {
    this.geometry = new THREE.BufferGeometry()

    let positions = new Float32Array(this.count * 3)
    let reference = new Float32Array(this.count * 2)
    let colors = new Float32Array(this.count * 3)
    let size = new Float32Array(this.count)

    let colorOptions = [
      new THREE.Color('#1FA6C5'),
      new THREE.Color('#70B878'),
      new THREE.Color('#0B3D30'),
      new THREE.Color('#057C8D'),
      new THREE.Color('#001D56'),
    ]

    let scaleSize

    switch(this.tier) {
      case 1:
        scaleSize = 30
        break

      case 2:
        scaleSize = 24
        break

      case 3:
        scaleSize = 15
        break

      default:
        scaleSize = 15
    }

    for (let i = 0; i < this.count; i++) {
      let x = Math.random()
      let y = Math.random()
      let z = Math.random()

      positions.set([x, y , z], i * 3)

      let u = (i % this.width) / this.width
      let v = ~~(i / this.width) / this.width

      reference.set([u, v], i * 2)

      let coin = Math.floor(Math.random() * colorOptions.length)
      let color = colorOptions[coin]

      colors.set([color.r, color.g, color.b], i * 3)

      size[i] = (1.0 + Math.random()) * scaleSize
    }

    let logoSurface = this.resources.items['dFlowModel'].scene.children[0]
    let logoPos = fillPositions(this.count, logoSurface)
    let spherePos = fillSphere(this.count, 0.85)
    let vennSurface = this.resources.items['vennModel'].scene.children[0]
    let vennPos = fillPositions(this.count, vennSurface)
    let sphereRingSurface = this.resources.items['sphereRingModel'].scene.children[0]
    let sphereRingPos = fillPositions(this.count, sphereRingSurface)
    let boxSurface = new THREE.Mesh(
      new THREE.BoxGeometry(1.5, 1.5, 1.5),
      new THREE.MeshNormalMaterial()
    )
    let boxPos = fillPositions(this.count, boxSurface)

    this.geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3))
    this.geometry.setAttribute('aReference', new THREE.BufferAttribute(reference, 2))
    this.geometry.setAttribute('aColor', new THREE.BufferAttribute(colors, 3))
    this.geometry.setAttribute('aSize', new THREE.BufferAttribute(size, 1))
    this.geometry.setAttribute('aLogoPos', new THREE.BufferAttribute(logoPos, 3))
    this.geometry.setAttribute('aSpherePos', new THREE.BufferAttribute(spherePos, 3))
    this.geometry.setAttribute('aVennPos', new THREE.BufferAttribute(vennPos, 3))
    this.geometry.setAttribute('aSphereRingPos', new THREE.BufferAttribute(sphereRingPos, 3))
    this.geometry.setAttribute('aBoxPos', new THREE.BufferAttribute(boxPos, 3))

    this.material = new THREE.ShaderMaterial({
      vertexShader: particleVertex,
      fragmentShader: particleFragment,
      uniforms: {
        uTime: { value: 0 },
        positionTexture: { value: null },
        uMorph1: { value: 0 },
        uMorph2: { value: 0 },
        uMorph3: { value: 0 },
        uMorph4: { value: 0 },
        uMorph5: { value: 0 },
        uMorph6: { value: 0 },
        uMorph7: { value: 0 },
        uResolution: { value: new THREE.Vector2(store.bounds.ww, store.bounds.wh) },
        uOpacity: { value: 0 },
      },
      transparent: true,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
      depthTest: false,
    })    

    this.mesh = new THREE.Points(this.geometry, this.material)
    this.mesh.frustumCulled = false

    this.scene.add(this.mesh)
  }

  update(t) {
    this.gpuCompute.compute()

    // Simulation uniforms
    this.positionVariable.material.uniforms.uTime.value = t
    this.sphereVariable.material.uniforms.uTime.value = t
    
    // Mesh uniforms
    this.material.uniforms.uTime.value = t
    this.material.uniforms.positionTexture.value = this.gpuCompute.getCurrentRenderTarget(this.positionVariable).texture

    // Mesh / sim uniforms that will be tweened on scroll trigger
    this.material.uniforms.uMorph1.value = store.progress[0].value
    this.material.uniforms.uMorph2.value = store.progress[1].value
    this.material.uniforms.uMorph3.value = store.progress[3].value
    this.material.uniforms.uMorph4.value = store.progress[4].value
    this.material.uniforms.uMorph5.value = store.progress[5].value
    this.material.uniforms.uMorph6.value = store.progress[6].value
    this.material.uniforms.uMorph7.value = store.progress[7].value
    // switches on and off position
    this.positionVariable.material.uniforms.uMorph2.value = store.progress[2].value
    this.positionVariable.material.uniforms.uMorph3.value = store.progress[7].value
  }

  animate() {
    this.tl = gsap.timeline()

    this.tl
      .to(this.defaultPosVariable.material.uniforms.uProgress, {
        value: 0.015,
        duration: 20,
        ease: 'power2.inOut',
      }, 0)
      .to(this.sphereVariable.material.uniforms.uProgress, {
        delay: 8,
        value: 0.2,
        duration: 20,
        ease: 'power2.inOut',
      }, 0)
      .from(this.velocityVariable.material.uniforms.uProgress, {
        value: 4,
        duration: 5,
        ease: 'power3.inOut',
      }, 2)
      .to(this.material.uniforms.uOpacity, {
        value: 1,
        duration: 1,
        ease: 'power3.inOut',
      }, 0)
  } 
}